import React, { useEffect, useState, useCallback } from 'react';
import { adminApi } from '../utils/api';
import { Card, Table, TR, TD, Badge, PlanBadge, StatusBadge, Btn, Input, Select, Modal, PageHeader, Pagination, fmtDate, fmtDateTime } from '../components/UI';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import RocketLoader from '../components/RocketLoader';

const UserDetailModal = ({ userId, onClose, onUpdate }) => {
  const [data, setData]     = useState(null);
  const [editing, setEditing] = useState({});
  const [saving, setSaving]   = useState(false);
  const [note, setNote]       = useState('');

  useEffect(() => {
    adminApi.getUser(userId).then(d => {
      setData(d);
      const exp = d.user.planExpiresAt ? new Date(d.user.planExpiresAt).toISOString().slice(0, 10) : '';
      setEditing({ plan: d.user.plan, status: d.user.status, name: d.user.name, planExpiresAt: exp });
      setNote(d.user.adminNote || '');
    });
  }, [userId]);

  const [ivTab, setIvTab] = useState('docs');

  const save = async () => {
    setSaving(true);
    try {
      const updated = await adminApi.updateUser(userId, { ...editing, adminNote: note });
      onUpdate(updated.user);
      setData(prev => ({ ...prev, user: updated.user }));
    } catch (e) { alert(e.message); }
    finally { setSaving(false); }
  };

  if (!data) return (
    <Modal title="User Details" onClose={onClose} width={640}>
      <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
        <RocketLoader size={140} message="Loading user…" />
      </div>
    </Modal>
  );

  const { user, docs, interviews = [] } = data;

  return (
    <Modal title={`User: ${user.name}`} onClose={onClose} width={640}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
        <div style={{ background: '#F8FAFC', borderRadius: 8, padding: 14 }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12 }}>
            {user.avatar
              ? <img src={user.avatar} alt="" style={{ width: 48, height: 48, borderRadius: '50%', objectFit: 'cover' }} />
              : <div style={{ width: 48, height: 48, borderRadius: '50%', background: 'linear-gradient(135deg,#EF1D28,#FB923C)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 700, fontSize: 18 }}>{user.name?.charAt(0)}</div>}
            <div>
              <div style={{ color: '#0F172A', fontWeight: 700 }}>{user.name}</div>
              <div style={{ color: '#64748B', fontSize: 12 }}>{user.email}</div>
            </div>
          </div>
          <div style={{ fontSize: 12, color: '#64748B' }}>Joined: <span style={{ color: '#64748B' }}>{fmtDate(user.createdAt)}</span></div>
          <div style={{ fontSize: 12, color: '#64748B' }}>Last login: <span style={{ color: '#64748B' }}>{fmtDateTime(user.lastLogin)}</span></div>
          <div style={{ fontSize: 12, color: '#64748B' }}>Login count: <span style={{ color: '#64748B' }}>{user.loginCount || 0}</span></div>
          {user.plan === 'pro' && user.billingCycle && (
            <div style={{ fontSize: 12, color: '#64748B' }}>Billing: <span style={{ color: '#64748B' }}>{user.billingCycle === 'annual' ? 'Yearly' : 'Monthly'}</span></div>
          )}
          <div style={{ fontSize: 12, color: '#64748B' }}>Documents: <span style={{ color: '#64748B' }}>{docs?.length || 0}</span></div>
          <div style={{ fontSize: 12, color: '#64748B' }}>Interviews: <span style={{ color: '#64748B' }}>{interviews?.length || 0}</span></div>
          {user.googleId && <div style={{ fontSize: 11, color: '#15803D', marginTop: 6 }}>✓ Google account</div>}
        </div>

        <div>
          <Input label="Name" value={editing.name || ''} onChange={v => setEditing(p => ({ ...p, name: v }))} />
          <Select label="Plan" value={editing.plan || 'free'} onChange={v => setEditing(p => ({ ...p, plan: v }))}
            options={[['free','Free'],['pro','Pro ⭐']]} />
          <Select label="Status" value={editing.status || 'active'} onChange={v => setEditing(p => ({ ...p, status: v }))}
            options={[['active','Active ✓'],['suspended','Suspended ✗'],['pending','Pending...']]} />
          <Input label="Pro Expires (date)" type="date" value={editing.planExpiresAt || ''} onChange={v => setEditing(p => ({ ...p, planExpiresAt: v, plan: v ? 'pro' : p.plan }))} />
          <div style={{ marginBottom: 12 }}>
            <div style={{ color: '#64748B', fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Quick Grant Pro Days</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {[7, 14, 30, 90].map(days => (
                <button key={days} type="button" onClick={() => {
                  const d = new Date(); d.setDate(d.getDate() + days);
                  setEditing(p => ({ ...p, plan: 'pro', planExpiresAt: d.toISOString().slice(0, 10) }));
                }} style={{ background: '#EFF6FF', color: '#2563EB', border: '1px solid #BFDBFE', borderRadius: 7, padding: '5px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                  +{days}d
                </button>
              ))}
              <button type="button" onClick={() => setEditing(p => ({ ...p, planExpiresAt: '' }))}
                style={{ background: '#F1F5F9', color: '#64748B', border: '1px solid #E2E8F0', borderRadius: 7, padding: '5px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                Clear
              </button>
            </div>
          </div>
        </div>
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={{ display: 'block', color: '#64748B', fontSize: 12, fontWeight: 600, marginBottom: 5 }}>Admin Note (internal)</label>
        <textarea value={note} onChange={e => setNote(e.target.value)}
          style={{ width: '100%', background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 7, padding: '9px 12px', color: '#0F172A', fontSize: 13, resize: 'vertical', minHeight: 70, outline: 'none', boxSizing: 'border-box' }}
          placeholder="Internal note about this user..." />
      </div>

      {(docs?.length > 0 || interviews?.length > 0) && (
        <div style={{ marginBottom: 16 }}>
          {/* Tab switcher */}
          <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
            {[['docs', `Documents (${docs?.length || 0})`], ['interviews', `Interviews (${interviews?.length || 0})`]].map(([id, label]) => (
              <button key={id} onClick={() => setIvTab(id)}
                style={{ fontSize: 12, fontWeight: 600, padding: '4px 12px', borderRadius: 20, border: `1px solid ${ivTab === id ? '#EF1D28' : '#E2E8F0'}`, background: ivTab === id ? '#FFF1E8' : '#F8FAFC', color: ivTab === id ? '#EF1D28' : '#64748B', cursor: 'pointer' }}>
                {label}
              </button>
            ))}
          </div>

          {ivTab === 'docs' && (
            <div style={{ maxHeight: 160, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
              {(docs || []).length === 0
                ? <div style={{ color: '#94A3B8', fontSize: 12, padding: '12px 0' }}>No documents</div>
                : docs.map(doc => (
                  <div key={doc._id} style={{ display: 'flex', justifyContent: 'space-between', background: '#F8FAFC', borderRadius: 6, padding: '7px 10px' }}>
                    <span style={{ color: '#334155', fontSize: 12 }}>{doc.data?.personalInfo?.fullName || doc.title || 'Untitled'} <span style={{ color: '#64748B' }}>({doc.type})</span></span>
                    <span style={{ color: '#64748B', fontSize: 11 }}>{fmtDate(doc.updatedAt)}</span>
                  </div>
                ))}
            </div>
          )}

          {ivTab === 'interviews' && (
            <div style={{ maxHeight: 160, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
              {(interviews || []).length === 0
                ? <div style={{ color: '#94A3B8', fontSize: 12, padding: '12px 0' }}>No interview sessions</div>
                : interviews.map(iv => (
                  <div key={iv._id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#F8FAFC', borderRadius: 6, padding: '7px 10px' }}>
                    <div>
                      <span style={{ color: '#334155', fontSize: 12, fontWeight: 600 }}>{iv.title || 'Untitled'}</span>
                      <span style={{ color: '#64748B', fontSize: 11, marginLeft: 6 }}>{iv.seniority} · {iv.questions?.length || 0} questions</span>
                    </div>
                    <span style={{ color: '#64748B', fontSize: 11 }}>{fmtDate(iv.createdAt)}</span>
                  </div>
                ))}
            </div>
          )}
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <Btn color="ghost" onClick={onClose}>Cancel</Btn>
        <Btn color="blue" onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</Btn>
      </div>
    </Modal>
  );
};

export const UsersPage = () => {
  const { admin }             = useAdminAuth();
  const [users, setUsers]     = useState([]);
  const [total, setTotal]     = useState(0);
  const [pages, setPages]     = useState(1);
  const [page, setPage]       = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');
  const [planFilter, setPlan] = useState('');
  const [statusFilter, setStatus] = useState('');
  const [selected, setSelected]   = useState([]);
  const [detailId, setDetailId]   = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminApi.getUsers({ page, limit: 20, search, plan: planFilter, status: statusFilter });
      setUsers(data.users);
      setTotal(data.total);
      setPages(data.pages);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [page, search, planFilter, statusFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [search, planFilter, statusFilter]);

  const toggleSelect = (id) => setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const toggleAll    = () => setSelected(selected.length === users.length ? [] : users.map(u => u._id));

  const bulkAction = async (action) => {
    if (!selected.length) return alert('Select users first');
    if (!window.confirm(`Apply "${action}" to ${selected.length} user(s)?`)) return;
    try {
      await adminApi.bulkUsers(selected, action);
      setSelected([]);
      load();
    } catch (e) { alert(e.message); }
  };

  const handleUpdate = (updated) => {
    setUsers(prev => prev.map(u => u._id === updated._id ? { ...u, ...updated } : u));
  };

  const quickStatus = async (id, status) => {
    try {
      const res = await adminApi.updateUser(id, { status });
      handleUpdate(res.user);
    } catch (e) { alert(e.message); }
  };

  const quickPlan = async (id, plan) => {
    const confirmMsg = plan === 'pro'
      ? 'Upgrade this user to Pro? They will get a confirmation email.'
      : 'Downgrade this user to Free? They will lose Pro access immediately and receive an email.';
    if (!window.confirm(confirmMsg)) return;
    try {
      const res = await adminApi.updateUser(id, { plan });
      handleUpdate(res.user);
    } catch (e) { alert(e.message); }
  };

  const deleteUser = async (id) => {
    if (!window.confirm('Permanently delete this user and all their documents?')) return;
    try {
      await adminApi.deleteUser(id);
      setUsers(prev => prev.filter(u => u._id !== id));
    } catch (e) { alert(e.message); }
  };

  const exportCSV = async () => {
    try {
      const data = await adminApi.getUsers({ page: 1, limit: 10000, search, plan: planFilter, status: statusFilter });
      const rows = [['Name','Email','Plan','Status','Joined','Last Login','Login Count','Docs']];
      data.users.forEach(u => rows.push([
        u.name, u.email, u.plan, u.status,
        u.createdAt ? new Date(u.createdAt).toLocaleDateString() : '',
        u.lastLogin ? new Date(u.lastLogin).toLocaleDateString() : '',
        u.loginCount || 0, u.docCount || 0,
      ]));
      const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g,'""')}"`).join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `marsresume-users-${new Date().toISOString().slice(0,10)}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { alert(e.message); }
  };

  return (
    <div>
      <PageHeader
        title={`Users (${total})`}
        subtitle="Manage user accounts, plans, and access"
        action={<Btn color="ghost" onClick={exportCSV}>⬇ Export CSV</Btn>}
      />

      {/* Filters */}
      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ flex: 1, minWidth: 200 }}>
            <label style={{ display: 'block', color: '#64748B', fontSize: 12, fontWeight: 600, marginBottom: 5 }}>Search</label>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name or email..."
              style={{ width: '100%', background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 7, padding: '9px 12px', color: '#0F172A', fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
          </div>
          <div style={{ minWidth: 130 }}>
            <label style={{ display: 'block', color: '#64748B', fontSize: 12, fontWeight: 600, marginBottom: 5 }}>Plan</label>
            <select value={planFilter} onChange={e => setPlan(e.target.value)}
              style={{ background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 7, padding: '9px 12px', color: '#0F172A', fontSize: 13, outline: 'none', cursor: 'pointer' }}>
              <option value="">All Plans</option>
              <option value="free">Free</option>
              <option value="pro">Pro</option>
            </select>
          </div>
          <div style={{ minWidth: 130 }}>
            <label style={{ display: 'block', color: '#64748B', fontSize: 12, fontWeight: 600, marginBottom: 5 }}>Status</label>
            <select value={statusFilter} onChange={e => setStatus(e.target.value)}
              style={{ background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 7, padding: '9px 12px', color: '#0F172A', fontSize: 13, outline: 'none', cursor: 'pointer' }}>
              <option value="">All Statuses</option>
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
            </select>
          </div>
          <Btn color="ghost" onClick={load}>🔄 Refresh</Btn>
        </div>
      </Card>

      {/* Bulk Actions */}
      {selected.length > 0 && (
        <div style={{ background: '#FFF7ED', border: '1px solid #FECACA', borderRadius: 8, padding: '10px 16px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <span style={{ color: '#FB923C', fontWeight: 600, fontSize: 13 }}>{selected.length} selected</span>
          <Btn color="ghost" size="sm" onClick={() => bulkAction('activate')}>✓ Activate</Btn>
          <Btn color="ghost" size="sm" onClick={() => bulkAction('suspend')}>⛔ Suspend</Btn>
          <Btn color="ghost" size="sm" onClick={() => bulkAction('setPro')}>⭐ Set Pro</Btn>
          <Btn color="ghost" size="sm" onClick={() => bulkAction('setFree')}>Set Free</Btn>
          <Btn color="ghost" size="sm" onClick={() => setSelected([])}>Clear</Btn>
        </div>
      )}

      <Card style={{ padding: 0 }}>
        <Table headers={['', 'User', 'Plan', 'Status', 'Docs', 'Joined', 'Last Login', 'Actions']}>
          {loading ? (
            <TR><TD colSpan={8}><div style={{ padding: 30, display: 'flex', justifyContent: 'center' }}><RocketLoader size={120} message="Loading users…" /></div></TD></TR>
          ) : users.map(user => (
            <TR key={user._id}>
              <TD style={{ width: 36 }}>
                <input type="checkbox" checked={selected.includes(user._id)} onChange={() => toggleSelect(user._id)}
                  style={{ cursor: 'pointer', width: 14, height: 14, accentColor: '#EF1D28' }} />
              </TD>
              <TD>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }} onClick={() => setDetailId(user._id)}>
                  {user.avatar
                    ? <img src={user.avatar} alt="" style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover' }} />
                    : <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'linear-gradient(135deg,#EF1D28,#FB923C)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 700, fontSize: 13, flexShrink: 0 }}>{user.name?.charAt(0)}</div>}
                  <div>
                    <div style={{ color: '#0F172A', fontWeight: 600, fontSize: 13 }}>{user.name}</div>
                    <div style={{ color: '#64748B', fontSize: 11 }}>{user.email}</div>
                  </div>
                </div>
              </TD>
              <TD><PlanBadge plan={user.plan} billingCycle={user.billingCycle} /></TD>
              <TD><StatusBadge status={user.status} /></TD>
              <TD style={{ color: '#64748B' }}>{user.docCount || 0}</TD>
              <TD style={{ color: '#64748B', fontSize: 12 }}>{fmtDate(user.createdAt)}</TD>
              <TD style={{ color: '#64748B', fontSize: 12 }}>{fmtDate(user.lastLogin)}</TD>
              <TD>
                <div style={{ display: 'flex', gap: 5, flexWrap: 'nowrap' }}>
                  <Btn size="sm" color="ghost" onClick={() => setDetailId(user._id)}>Edit</Btn>
                  <Btn size="sm" color={user.plan === 'pro' ? 'ghost' : 'purple'} onClick={() => quickPlan(user._id, user.plan === 'pro' ? 'free' : 'pro')}>
                    {user.plan === 'pro' ? 'Downgrade' : '⭐ Pro'}
                  </Btn>
                  <Btn size="sm" color={user.status === 'active' ? 'red' : 'green'} onClick={() => quickStatus(user._id, user.status === 'active' ? 'suspended' : 'active')}>
                    {user.status === 'active' ? 'Suspend' : 'Activate'}
                  </Btn>
                  {admin?.role === 'superadmin' && (
                    <Btn size="sm" color="red" onClick={() => deleteUser(user._id)}>🗑</Btn>
                  )}
                </div>
              </TD>
            </TR>
          ))}
        </Table>
      </Card>

      <Pagination page={page} pages={pages} onChange={setPage} />

      {detailId && (
        <UserDetailModal userId={detailId} onClose={() => setDetailId(null)} onUpdate={handleUpdate} />
      )}
    </div>
  );
};
